import _ from 'lodash';
import formUrlEncoded from 'form-urlencoded';
import { AxiosResponse } from 'axios';
import { parsePhoneNumber } from 'libphonenumber-js';

import { networkService } from '@project/services';
import { CommunicationMethodEnum } from '@project/enums';
import {
  AppleExternalResponse,
  CanSignUpDto,
  ConfirmAccountOtp,
  FacebookExternalResponse,
  ForgotPasswordOtpDto,
  ForgotPasswordOtpProps,
  ForgotPasswordProps,
  GoogleExternalResponse,
  LoginProps,
  OnboardBusinessProps,
  OnboardIndividualProps,
  OtpDto,
  OtpProps,
  ResendAccountOtp,
  ResendOtpDto,
  ResendOtpProps,
  ResetPasswordProps,
  ServiceResult,
  SignUpProps,
  SignUpReferralProps,
  Utm,
  VerifyAccountProps,
  VerifyNewEmailProps,
} from '@project/types';

import authUrls from './user-auth.urls';
import authUtils from './user-auth.utils';
import { SignUpV2Dto, SignUpV2FleetDto, SignUpV2IndividualDto } from './types';

const config = {
  headers: {
    Accept: '*/*',
    'Content-Type': 'application/x-www-form-urlencoded',
  },
};

const login = (formData: LoginProps) => {
  const loginUrl = authUrls.tokenUrl();
  const oAuthData = authUtils.constructOAuthSignInData(formData);

  return networkService
    .post(loginUrl, formUrlEncoded(oAuthData), config)
    .then(authUtils.storeAccessAndRefreshTokens);
};

const signUp = (formData: SignUpProps & Utm) => {
  const signUpUrl = authUrls.signUpUrl();
  return networkService.post(signUpUrl, formData);
};

const mapFleet = (fleet: OnboardBusinessProps): SignUpV2FleetDto | null => {
  if (fleet.businessEntities.length === 0) {
    return null;
  }
  if (fleet.businessEntities.length !== 0 && fleet.businessEntities[0].entityName === '') {
    return null;
  }

  return {
    fleetEntities: fleet.businessEntities.map((be) => ({
      name: be.entityName,
      address: be.address,
      companyRegistrationNumber: be.companyRegistration,
      offenderIdentifiers: be.links.map((l) => ({
        value: l.value,
        type: l.identifierType,
      })),
      vatNumber: be.vatNo,
    })),
  };
};

const mapIndividual = (individual: OnboardIndividualProps): SignUpV2IndividualDto | null => {
  if (individual.idNumber === '' || individual.idNumber === null) {
    return null;
  }
  return {
    offenderIdentifier: {
      type: individual.identifierType,
      value: individual.idNumber,
    },
    familyMembers: individual.familyMembers
      .filter((s) => s.idNumber !== '' && s.firstName !== '' && s.lastName !== '')
      .map((s) => ({
        firstName: s.firstName,
        lastName: s.lastName,
        offenderIdentifier: {
          type: s.identifierType,
          value: s.idNumber,
        },
      })),
  };
};

const signUpv2 = (
  accountInfo: SignUpProps,
  individual: OnboardIndividualProps,
  fleet: OnboardBusinessProps,
  utm: Utm,
  referral: SignUpReferralProps,
  confirmationMethod: CommunicationMethodEnum = CommunicationMethodEnum.email,
) => {
  const signUpUrl = `${authUrls.signUpV2()}?confirmationMethod=${confirmationMethod}`;

  const payload: SignUpV2Dto = {
    firstName: accountInfo.firstName,
    lastName: accountInfo.lastName,
    email: accountInfo.email,
    password: accountInfo.password,
    confirmPassword: accountInfo.confirmPassword,
    cellphoneNumber: parsePhoneNumber(
      `+${accountInfo.code}${accountInfo.cellphone}`,
    ).formatInternational(),
    whatsAppPhoneNumber:
      accountInfo.whatsAppCode && accountInfo.whatsAppCellphone
        ? (() => {
            try {
              return parsePhoneNumber(
                `+${accountInfo.whatsAppCode}${accountInfo.whatsAppCellphone}`,
              ).formatInternational();
            } catch (error) {
              return '';
            }
          })()
        : '',
    termsChecked: accountInfo.termsChecked,
    province:
      accountInfo.province === '' ||
      accountInfo.province === null ||
      accountInfo.province === undefined
        ? null
        : Number(accountInfo.province),
    whatsAppOptedIn: accountInfo.whatsAppOptedIn,
    profiles: {
      individual: mapIndividual(individual),
      fleet: mapFleet(fleet),
    },
    utmSource: utm.utmSource,
    utmMedium: utm.utmMedium,
    utmCampaign: utm.utmCampaign,
    utmContent: utm.utmContent,
    utmTerm: utm.utmTerm,
    referrerEmail: referral.referrerEmail,
    referralEmail: referral.referralEmail,
  };

  return networkService.post(signUpUrl, payload);
};

const canSignUp = async (email: string) => {
  const canSignUpUrl = authUrls.canSignUpUrl();
  const response = await networkService
    .get<ServiceResult<CanSignUpDto>>(canSignUpUrl, {
      params: {
        email,
      },
    })
    .catch(Promise.reject);

  return response.data.data;
};

const verifyAccount = (formData: VerifyAccountProps) => {
  const verifyAccountUrl = authUrls.verifyAccountUrl();
  return networkService.post(verifyAccountUrl, formData);
};

const verifyNewEmail = (formData: VerifyNewEmailProps) => {
  const verifyNewEmailUrl = authUrls.verifyNewEmailUrl();

  return networkService.post(verifyNewEmailUrl, formData);
};

const logout = () => {
  return authUtils.removeAccessAndRefreshTokens();
};

const forgotPassword = (formData: ForgotPasswordProps) => {
  const forgotPasswordUrl = authUrls.forgotPasswordUrl();
  return networkService.post(forgotPasswordUrl, formData).catch((err) => {
    return Promise.reject(err);
  });
};

const facebookExternalSignIn = async (formData: FacebookExternalResponse) => {
  const facebookExternal = authUrls.facebookExternalSignIn();

  const response = await networkService.post(facebookExternal, formData);

  if (response.data && (response.data as any).error_description) {
    const errorMessage = (response.data as any).error_description;
    if (errorMessage.startsWith('user_not_found:')) {
      const email = errorMessage.split(':')[1];
      sessionStorage.setItem('preSignUpEmail', email);

      return false;
    }
  }
  await authUtils.storeAccessAndRefreshTokens(response);

  return true;
};

const googleExternalSignIn = async (formData: GoogleExternalResponse) => {
  const googleExternal = authUrls.googleExternalSignIn();

  const response = await networkService.post(googleExternal, formData);

  if (response.data && (response.data as any).error_description) {
    const errorMessage = (response.data as any).error_description;
    if (errorMessage.startsWith('user_not_found:')) {
      const email = errorMessage.split(':')[1];
      sessionStorage.setItem('preSignUpEmail', email);

      return false;
    }
  }
  await authUtils.storeAccessAndRefreshTokens(response);

  return true;
};

const appleExternalSignIn = async (formData: AppleExternalResponse) => {
  const appleExternal = authUrls.appleExternalSignIn();

  const response = await networkService.post(appleExternal, formData);

  if (response.data && (response.data as any).error_description) {
    const errorMessage = (response.data as any).error_description;
    if (errorMessage.startsWith('user_not_found:')) {
      const email = errorMessage.split(':')[1];
      sessionStorage.setItem('preSignUpEmail', email);

      return false;
    }
  }

  await authUtils.storeAccessAndRefreshTokens(response);
  return true;
};

const forgotPasswordOtp = (formData: ForgotPasswordOtpProps) => {
  const forgotPasswordOtpUrl = authUrls.forgotPasswordOtpUrl();
  const forgotPasswordOtpDto: ForgotPasswordOtpDto = {
    PhoneNumber: `${formData.code}${formData.cellphone}`,
  };

  return networkService.post(forgotPasswordOtpUrl, forgotPasswordOtpDto).catch((err) => {
    return Promise.reject(err);
  });
};

const confirmOtp = (formData: OtpProps) => {
  const forgotPasswordOtpUrl = authUrls.resetPasswordOtpUrl();
  const otpDto: OtpDto = {
    Otp: formData.otp,
    Password: formData.password,
    ConfirmPassword: formData.confirmPassword,
    PhoneNumber: formData.phoneNumber,
  };

  return networkService.post(forgotPasswordOtpUrl, otpDto).catch((err) => {
    return Promise.reject(err);
  });
};

const resendPasswordOtp = (formData: ResendOtpProps) => {
  const forgotPasswordOtpUrl = authUrls.resendPasswordOtpUrl();
  const otpDto: ResendOtpDto = {
    PhoneNumber: formData.phoneNumber,
  };

  return networkService.post(forgotPasswordOtpUrl, otpDto).catch((err) => {
    return Promise.reject(err);
  });
};

const normalizePhoneNumber = (phoneNumber: string): string => {
  let digits = (phoneNumber || '').replace(/\D/g, '');
  if (digits.startsWith('27')) {
    digits = digits.substring(2);
  }
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }
  return `27${digits}`;
};

const accountConfirmationOtp = (formData: ConfirmAccountOtp) => {
  const forgotPasswordOtpUrl = authUrls.confirmAccountOtpUrl();
  const otpDto: OtpDto = {
    Otp: formData.otp,
    Email: formData.email,
    PhoneNumber: normalizePhoneNumber(formData.phoneNumber),
  };

  return networkService.post(forgotPasswordOtpUrl, otpDto).catch((err) => {
    return Promise.reject(err);
  });
};

const resendAccountOtp = (formData: ResendAccountOtp) => {
  const forgotPasswordOtpUrl = authUrls.resendAccountOtpUrl();
  const otpDto: ResendOtpDto = {
    UserConfirmationMethod: formData.userConfirmationMethod,
    PhoneNumber: formData.phoneNumber,
  };

  return networkService.post(forgotPasswordOtpUrl, otpDto).catch((err) => {
    return Promise.reject(err);
  });
};

const resetPassword = (formData: ResetPasswordProps) => {
  const resetPasswordUrl = authUrls.resetPasswordUrl();
  return networkService.post(resetPasswordUrl, formData).catch((err) => {
    return Promise.reject(err);
  });
};

const doTokensExistInStorage = () => {
  const _trueIfBothExist = (accessToken: string | null, refreshToken: string | null) =>
    !_.isNull(accessToken) && !_.isNull(refreshToken);

  return authUtils
    .getAccessAndRefreshTokens()
    .then(([accessToken, refreshToken]) => _trueIfBothExist(accessToken, refreshToken));
};

const unlock = (token: string) => {
  const forgotPasswordUrl = authUrls.unlockAccountUrl();
  return networkService.post(forgotPasswordUrl, token).catch((err) => {
    return Promise.reject(err);
  });
};

const getTokenForInitialPassword = (email: string) => {
  const initialPasswordURL = `${authUrls.initialPasswordUrl()}?Email=${email}`;
  return networkService
    .get(initialPasswordURL)
    .then((apiResponse: AxiosResponse<ServiceResult<string>>) => apiResponse.data.data)
    .catch((error) => {
      return Promise.reject(error);
    });
};

export default {
  login,
  logout,
  signUp,
  unlock,
  signUpv2,
  canSignUp,
  confirmOtp,
  verifyAccount,
  resetPassword,
  verifyNewEmail,
  forgotPassword,
  resendAccountOtp,
  resendPasswordOtp,
  forgotPasswordOtp,
  appleExternalSignIn,
  googleExternalSignIn,
  doTokensExistInStorage,
  accountConfirmationOtp,
  facebookExternalSignIn,
  getTokenForInitialPassword,
};
